import React, {memo, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {Box, Chip, CircularProgress, Tooltip} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {isImageFile} from "../../resources/utils/imageUtils";

const documentRemovalIcon = (status, id) => (
  <Tooltip placement="top" title={status === "uploading" ? "Uploading" : "Remove"}>
    {status === "uploading" ?
      (
        <CircularProgress size={16} id={`document-attach-uploading-${id}`} />
      ) :
      (
        <CancelOutlinedIcon fontSize="small" id={`document-attach-remove-${id}`} />
      )}
  </Tooltip>
);

const DocumentAttachChip = memo(({attachment, onRemove, maxFiles}) => {
  const [thumbUrl, setThumbUrl] = useState();
  const isImage = isImageFile(attachment?.fileName);

  useEffect(() => {
    if (!isImage || !attachment?.file) {
      setThumbUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(attachment.file);
    setThumbUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [isImage, attachment?.file]);

  if (!attachment) {
    return null;
  }

  const errorMessage = attachment.status === "error" ? attachment.error || "Upload failed" : "";
  const tooltipMessage =
    attachment.status === "skipped" ?
      `Skipped — max ${maxFiles || "number of"} files` :
      errorMessage;
  const chipColor =
    attachment.status === "error" || attachment.status === "skipped" ? "error" : "default";
  const handleRemove = onRemove ? () => onRemove(attachment.id) : undefined;

  const icon = thumbUrl ?
    (
      <img
        src={thumbUrl}
        alt=""
        style={{width: 20, height: 20, borderRadius: 2, objectFit: "cover", marginLeft: 4}}
      />
    ) :
    isImage ?
      (
        <ImageOutlinedIcon fontSize="small" />
      ) :
      (
        <InsertDriveFileOutlinedIcon fontSize="small" />
      );

  return (
    <Box sx={{mb: 0.5, display: "flex"}}>
      <Tooltip placement="top" title={tooltipMessage} arrow>
        <Chip
          icon={icon}
          label={attachment.fileName}
          deleteIcon={documentRemovalIcon(attachment.status, attachment.id)}
          onDelete={attachment.status === "uploading" ? () => {} : handleRemove}
          variant="outlined"
          size="small"
          color={chipColor}
          id={`document-attach-chip-${attachment.id}`}
        />
      </Tooltip>
    </Box>
  );
});

DocumentAttachChip.displayName = "DocumentAttachChip";

DocumentAttachChip.propTypes = {
  attachment: PropTypes.shape({
    docId: PropTypes.string,
    fileName: PropTypes.string,
    file: PropTypes.object,
    pageCount: PropTypes.number,
    status: PropTypes.oneOf(["uploading", "pending", "ready", "error", "skipped"]),
    error: PropTypes.string,
  }),
  onRemove: PropTypes.func,
  maxFiles: PropTypes.number,
};

export default DocumentAttachChip;
